/**
 * Capabilities query
 * @module @services/entitlements
 */

import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  getI18nService,
  useLanguageSwitch,
  I18N_DEFAULT_LANGUAGE,
  type Language,
} from '@infra/i18n';
import { getUserRoles, getUserPublicId } from '@infra/auth';
import type { HttpError } from '@infra/http';
import { USER_ROLE_CLAIMS } from '@shared/constants';
import { ROLEPLAY_MODES, type RoleplayMode } from '@shared/types';
import { getCurrentUser } from '../storage';
import { getMeCapabilities } from './capabilities.api';
import type { MeCapabilities } from './capabilities.types';
import { ME_CAPABILITIES_QUERY_KEY } from './capabilities.types';

/**
 * What the signed-in caller may do, as the server answers it.
 *
 * The server is the gate: every create re-checks this independently, so a stale or
 * missing answer here cannot grant anything. What it decides is whether the UI offers
 * an action the server would refuse.
 */
export function useMeCapabilitiesQuery() {
  return useQuery({
    queryKey: ME_CAPABILITIES_QUERY_KEY(getUserPublicId()),
    queryFn: (): Promise<MeCapabilities> => getMeCapabilities(),
    // "What may I do" has no answer for a caller who is nobody. Since
    // `useSyncAllowedLanguages` runs at the app root, leaving this ungated would fire
    // the request on the public landing page and answer 401 on every visit.
    enabled: Boolean(getCurrentUser()),
    // Overrides the client-wide `retry: false`. The create-permission consumers read
    // an unknown answer as "no", so one blip at boot would hide an entitled user's
    // create buttons. A 401 is a real answer, not a blip, so it is not retried.
    retry: (failureCount, error) => failureCount < 2 && (error as HttpError).status !== 401,
  });
}

/**
 * Whether to offer the persona-create entry points.
 *
 * Unknown counts as "no": while the answer is in flight, and if the request failed, the
 * entry stays hidden. Showing it on an unknown answer is what produces the button that
 * 403s on click; hiding it costs an entitled user one reload, and the route itself stays
 * reachable for anyone who has the URL.
 */
export function useCanCreatePersona(): boolean {
  const { data } = useMeCapabilitiesQuery();
  return data?.canCreatePersona ?? false;
}

/**
 * Whether to offer the scenario-create entry points. Same rule as
 * {@link useCanCreatePersona}: unknown counts as "no".
 */
export function useCanCreateScenario(): boolean {
  const { data } = useMeCapabilitiesQuery();
  return data?.canCreateScenario ?? false;
}

/**
 * Whether the entitlement answer has landed, either way.
 *
 * A screen holds its start button on this so a click in the fetch window cannot post
 * a mode the account turns out not to have. Waiting is only worth one attempt: the
 * retries take tens of seconds, and a network that cannot answer this cannot carry
 * the session POST either, so a first failure settles and the screen falls open.
 */
export function useCapabilitiesSettled(): boolean {
  const { isPending, isFetched, fetchStatus, failureCount } = useMeCapabilitiesQuery();
  // A query gated off by `enabled` reports isPending forever; it is idle, not in
  // flight, and an anonymous caller has nothing to wait for.
  if (fetchStatus === 'idle' && !isFetched) return true;
  return failureCount > 0 || !isPending;
}

/**
 * Roleplay modes the start screens may offer.
 *
 * NO ANSWER fails open: hiding every mode would leave the account unable to start
 * anything, and the server refuses a mode it did not grant whatever the UI offered.
 * An answer naming modes this build does not know is a different thing — it comes
 * back empty, because reading a restriction as permission is how the strictest
 * configuration would render as the most permissive screen.
 */
export function useAllowedModes(): RoleplayMode[] {
  const { data } = useMeCapabilitiesQuery();
  const allowed = data?.allowedModes;
  // Memoised so callers can depend on the array itself without re-running on
  // every render of the screen that reads it.
  return useMemo(() => {
    if (!allowed?.length) return [...ROLEPLAY_MODES];
    return ROLEPLAY_MODES.filter((mode) => allowed.includes(mode));
  }, [allowed]);
}

/**
 * Narrows the language picker to the account's allowed languages and moves the reader off a
 * disallowed one. Call once, at the app root. An unknown answer lifts the restriction;
 * admins are exempt for the reading language.
 */
export function useSyncAllowedLanguages(): void {
  const { data } = useMeCapabilitiesQuery();
  const switchLang = useLanguageSwitch();
  const isAdmin = getUserRoles().includes(USER_ROLE_CLAIMS.ADMIN);
  const allowed = isAdmin ? undefined : data?.allowedLanguages;

  useEffect(() => {
    const i18n = getI18nService();
    i18n.setAllowedLanguages(allowed ?? null);
    if (!allowed?.length) return;
    if (allowed.includes(i18n.getLanguage())) return;

    const supported = allowed.filter((code): code is Language => i18n.isLanguageSupported(code));
    // The product default wins when allowed; otherwise the first allowed language.
    const fallback = supported.includes(I18N_DEFAULT_LANGUAGE)
      ? I18N_DEFAULT_LANGUAGE
      : supported[0];
    // The allowlist names no shipped language: stay put (the picker already failed open).
    if (!fallback) return;
    // Persisted so later logins do not start on a disallowed language.
    void switchLang(fallback).catch((error) => {
      console.error('Failed to move off a language this account may not use:', error);
    });
  }, [allowed, switchLang]);
}
